import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getChargingStationInfo, checkOneClickChargeEligibility } from "../services/apiService";
import { useErrorHandler } from "../utils/errorHandler";
import analyticsService from "../services/analyticsService";

const REDIRECTION_PAGE_URL = process.env.REACT_APP_REDIRECTION_PAGE_URL;


export default function ChargeComponent() {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const partner = queryParams.get("partner");
  const connectorParam = queryParams.get("connector");
  const serial = queryParams.get("serial");

  const [cpData, setCpData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStoreRedirect, setShowStoreRedirect] = useState(false);
  const { ref: reference } = useParams();
  const navigate = useNavigate();

  const hasProcessedRedirection = useRef(false);

  useEffect(() => {
    if (hasProcessedRedirection.current) {
      return;
    }
    hasProcessedRedirection.current = true;

    const fetchChargePointDetails = async () => {
      try {
        // Récupérer les informations de la borne
        const evcpData = await getChargingStationInfo(reference);
        setCpData(evcpData);
        analyticsService.pushEvent('station_loaded', {
          station_id: evcpData.reference,
          partner: partner || 'QOVOLTIS',
        });

        // Vérifier l'éligibilité au One-Click Charge
        const eligibilityData = await checkOneClickChargeEligibility(evcpData.reference);

        if (!eligibilityData.eligibility) {
          analyticsService.pushEvent('station_not_eligible', { station_id: evcpData.reference });
          window.open(REDIRECTION_PAGE_URL, '_blank', 'noopener,noreferrer');
          setShowStoreRedirect(true);
          setIsLoading(false);
          return;
        }

        // Détection d'une session active sur la borne (re-scan QR)
        // → écran de vérification au lieu du parcours de démarrage
        const activeConnector = evcpData.connectors.find(
          connector => connector.hasTransaction && connector.type !== "DOMESTIC_E"
        );

        if (activeConnector) {
          analyticsService.pushEvent('active_session_detected', {
            station_id: evcpData.reference,
            connector_id: activeConnector.reference,
          });
          navigate("/resume-session", {
            state: {
              evcpData,
              connector: activeConnector,
              reference,
              partner: partner || "QOVOLTIS",
            },
          });
          return;
        }

        // filtrer les connecteurs pour masquer ceux de type E
        const filteredConnectors = evcpData.connectors.filter(
          connector => connector.type !== "DOMESTIC_E"
        );

        if (connectorParam) {
          // Trouver le connecteur correspondant dans la liste des connecteurs
          const selectedConnector = filteredConnectors.find(
            connector => connector.reference === connectorParam
          );
          
          if (selectedConnector) {
            navigate("/payment", {
              state: { 
                connector: selectedConnector, 
                evcpData,
                reference,
                partner: partner || "QOVOLTIS"
              },
            });
            return;
          }
        }

        // Si éligible, rediriger vers la page des connecteurs
        if (partner === 'SYDER' || filteredConnectors.length > 1 ) {
          navigate(`/connectors/${reference}`, {
            state: { 
              evcpData, 
              eligibilityData,
              partner: partner || "QOVOLTIS"
            },
          });
        } else {
          navigate("/payment", {
            state: { 
              connector: filteredConnectors[0], 
              evcpData,
              reference,
              partner: partner || "QOVOLTIS"
            },
          });
        }
      } catch (err) {
        console.error("Erreur:", err);
        const isNotFound = err?.response?.status === 404;
        analyticsService.pushEvent(isNotFound ? 'station_not_found' : 'station_load_error', {
          station_id: reference,
          error_type: err?.response?.status || 'unknown',
        });
        setError(handleError(err));
        setIsLoading(false);
        navigate("/error", {
          state: { errorText: handleError(err) },
        });
      }
    };

    if (reference) {
      analyticsService.pushPageView('scan_to_charge_landing', { station_id: reference });
      fetchChargePointDetails();
    }
  }, [navigate, reference, partner, serial, connectorParam, handleError, t]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center space-y-8">
        <div className="flex space-x-1">
          {[...Array(6)].map((_, i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-indigo-600 animate-bounce"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
        <p className="text-black font-semibold text-lg">{t("loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 pt-36">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong className="font-bold">{t("errorTitle")}</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  if (showStoreRedirect) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="bg-white flex flex-col ">
            <div className="w-20 h-20 mx-auto flex items-center justify-center">
              <img src="/Danger_Circle_Red.svg" alt={t("errorIconAlt")} className="image-container"/>
            </div>
            <h2 className="error-title mx-auto">{t("genericErrorTitle")}</h2>
            <p className="displayed-error-message mx-auto mb-2">
              {t("notEligibleForOneClick")}
            </p>
          </div>
          <div>
            <p className="text-gray-700 font-medium mb-1">
              {t("storeRedirect.downloadTitle")}
            </p>
            <p className="text-gray-500 text-sm mb-4">
              {t("storeRedirect.downloadDescription")}
            </p>
            <div className="flex justify-center space-x-4">
              <img
                src="/google-play-badge.svg"
                alt="Google Play"
                className="h-10 cursor-pointer"
                onClick={() => {
                  analyticsService.pushEvent('app_download_tapped', { platform: 'android' });
                  window.open('https://play.google.com/store/apps/details?id=com.qovoltis.client', '_blank');
                }}
              />
              <img
                src="/app-store-badge.svg"
                alt="App Store"
                className="h-10 cursor-pointer"
                onClick={() => {
                  analyticsService.pushEvent('app_download_tapped', { platform: 'ios' });
                  window.open('https://apps.apple.com/fr/app/qovoltis/1524983586', '_blank');
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}